import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ExcelService } from 'app/misc/excel.service';
import { Authorizer } from 'app/modules/authorizers/_state/authorizers.interfaces';
import { AuthorizerService } from 'app/modules/authorizers/authorizers.service';
import { Bank, MerchantWallet } from 'app/modules/products/_state/products.interfaces';
import { NotifyDto } from 'app/modules/transactions/_state/txn.dtos';
import {
  AppState,
  selectAccountBalance,
  selectAccountBalanceError,
  selectAccountBalanceLoading,
  selectCategoryRecipients,
  selectCategoryRecipientsError,
  selectCategoryRecipientsLoading,
  selectCountries,
  selectCountriesError,
  selectCountriesLoading,
  selectWallets,
  selectWalletsError,
  selectWalletsLoading
} from 'app/reducers';
import { MiscService } from 'app/shared/misc.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as productsActions from '../../products/_state/products.actions';
import * as transactionsActions from '../../transactions/_state/txn.actions';
import * as bulkPayActions from '../_state/bulk-pay.actions';
import { EditRecipientDto, RecipientBulkDto, RecipientDto } from '../_state/bulk-pay.dtos';
import { Country, Recipient } from '../_state/bulk-pay.interfaces';
import { BulkPayService } from '../bulk-pay.service';
import { CreateReceiptsComponent } from '../create-receipts/create-receipts.component';

@Component({
  selector: 'app-category-recipients',
  templateUrl: './category-recipients.component.html',
  styleUrls: ['./category-recipients.component.css']
})
export class CategoryRecipientsComponent extends CreateReceiptsComponent implements OnInit {

  constructor(
    protected store: Store<AppState>,
    protected actions$: Actions,
    protected router: Router,
    protected toastr: ToastrService,
    protected route: ActivatedRoute,
    public miscService: MiscService,
    protected bulkpayService: BulkPayService,
    protected excelService: ExcelService,
    protected authorizerService: AuthorizerService
  ) {
    super(store, actions$, toastr, router, miscService, excelService, bulkpayService)
  }

  categoryId: string;
  categoryName: string;
  currentPaymentId: string;
  otpSid: string;
  currency: string = this.miscService.getMerchantDetails().currency.currency_code
  countryISO2: string = this.miscService.getMerchantDetails().currency.iso_code_2
  paymentList: Recipient[] = [];
  recipients: Recipient[];
  selectedRecipient: Recipient;
  wallets: MerchantWallet[];
  countries: Country[];
  selectedWallet: MerchantWallet;
  paymentForm: FormGroup;
  otpForm: FormGroup;
  isPaymentPage: boolean;
  modalOpen: boolean;
  otpModalOpen: boolean;
  isUpdating: boolean;
  isNotifying: boolean;
  reprocess: boolean;
  isDeleting: boolean;
  sendingOTP: boolean;
  bulkPayInProcess: boolean;
  isUpdatingTransaction: boolean;
  isUpdatingTransactionFailed: boolean;
  checkingSignatures: boolean;
  authorizersNotifed: boolean;
  sufficientBalance: boolean;
  totalToPay: any;
  transactionsDone: number = 0;
  creatingBulk: boolean = false;
  protected unsubscribe$ = new Subject<void>();

  recipients$ = this.store.pipe(select(selectCategoryRecipients))
  recipientsLoading$ = this.store.pipe(select(selectCategoryRecipientsLoading))
  recipientsError$ = this.store.pipe(select(selectCategoryRecipientsError))

  wallets$ = this.store.pipe(select(selectWallets))
  walletsLoading$ = this.store.pipe(select(selectWalletsLoading))
  walletsError$ = this.store.pipe(select(selectWalletsError))

  accountBalance$ = this.store.pipe(select(selectAccountBalance))
  accountBalanceLoading$ = this.store.pipe(select(selectAccountBalanceLoading))
  accountBalanceError$ = this.store.pipe(select(selectAccountBalanceError))

  countries$ = this.store.pipe(select(selectCountries))
  countriesLoading$ = this.store.pipe(select(selectCountriesLoading))
  countriesError$ = this.store.pipe(select(selectCountriesError))

  paymentModalOpen: boolean = false;
  permissionToManage: boolean = false;
  isMerchant: boolean = false;
  userPermissions: any[] = [];
  kycDetails: any;
  kycData: any = null;
  kycForm: FormGroup;
  bankTransferData: any = null;
  bankTransferConfigKey: string = '';
  ezremitData: any = null;
  ngOnInit(): void {
    this.userPermissions = JSON.parse(localStorage.getItem('user-permissions'));
    if (this.userPermissions) {
      this.permissionChecker(this.userPermissions, 'Payment');
      this.isMerchant = false;
    } else {
      this.isMerchant = true
    }
    this.categoryId = this.route.snapshot.paramMap.get('categoryId');
    this.categoryName = this.route.snapshot.paramMap.get('categoryName');
    this.fetchRecipients();
    this.recipients$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: Recipient[]) => this.recipients = res)
    this.recipientForm = new FormGroup({
      receiptAccountType: new FormControl('', Validators.required),
      price: new FormControl(''),
      bank: new FormControl(''),
      accountNumber: new FormControl(''),
      xcelAccount: new FormControl(''),
      recipientName: new FormControl('', Validators.required),
      // receiptCategory: new FormControl('', Validators.required)
    })
    this.paymentForm = new FormGroup({
      description: new FormControl(''),
      walletToDebit: new FormControl('', Validators.required)
    })
    this.otpForm = new FormGroup({
      otp: new FormControl('', Validators.required)
    })
    this.kycForm = new FormGroup({
      sender_first_name: new FormControl('', Validators.required),
      sender_last_name: new FormControl('', Validators.required),
      sender_nationality: new FormControl(this.countryISO2 || '', Validators.required),
      sender_birthdate: new FormControl('', Validators.required),
      sender_city: new FormControl('', Validators.required),
      sender_address: new FormControl('', Validators.required),
      sender_permanent_address: new FormControl('', Validators.required),
      sender_occupation: new FormControl('', Validators.required),
      identity_number: new FormControl('', Validators.required),
      document_issue_date: new FormControl('', Validators.required),
      document_expiration_date: new FormControl('', Validators.required),
    })
    this.banks$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: Bank[]) => {
      if (res) {
        this.banks = res
        this.selectedBank = this.banks.find(bank => bank.bank_code == this.recipientForm.get('bank').value)
      }
    })
    this.countries$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: Country[]) => this.countries = res)
    this.wallets$.pipe(takeUntil(this.unsubscribe$)).subscribe((res: MerchantWallet[]) => this.wallets = res)
    this.fetchRecipientsCategories();
    this.fetchCountries();
    this.fetchMerchantWallets();
    this.onValidateBankAccountSuccessful();
    this.onValidateXcelAccountSuccessful();
    this.onEditRecipientFailed();
    this.onEditRecipientSuccessful();
    this.onFetchBalanceSuccessful();
    this.onNotifyFailed();
    this.onNotifySuccessful();
    this.onCheckSignaturesSuccessful();
    this.onCheckSignaturesFailed();
    this.onSendOTPSuccessful();
    this.onUpdateTransactionStatusFailed();
    this.onUpdateTransactionStatusSuccessful();
    this.onDeleteRecipientFailed();
    this.onDeleteRecipientSuccessful()
  }
  permissionChecker(permissions: any[], permission: string): boolean {
    if (permissions) {
      for (const obj of permissions) {
        if (obj.hasOwnProperty(permission)) {
          const manage = obj['Payment']['manage information'];
          if (manage == 1) {
            this.permissionToManage = true;
          }

          return true;
        }
      }
      return false;
    }
  }
  page = 1
  limit = 100
  totalItems
  recipientsLoading: boolean;

  onPageChange(event: PageEvent): void {
    const currentPage = event.pageIndex + 1;
    this.limit = event.pageSize;
    this.fetchRecipients(currentPage, this.limit);
  }

  // API CALLS
  fetchBanks = () => this.store.dispatch(productsActions.fetchBanks())

  fetchAccountBalance = () => this.paymentForm.get('walletToDebit').invalid ? null : this.store.dispatch(bulkPayActions.fetchAccountBalance({ accountIdentifier: this.paymentForm.get('walletToDebit').value }))

  fetchCountries = () => this.store.dispatch(bulkPayActions.fetchCountries())

  fetchMerchantWallets = () => this.store.dispatch(productsActions.fetchWallets())

  // fetchRecipients = () => this.store.dispatch(bulkPayActions.fetchRecipientsByCategory({categoryId: this.categoryId}))
  fetchRecipients = (page = this.page, limit = this.limit) => {
    this.recipients = null
    this.recipientsLoading = true;
    this.bulkpayService.getRecipientsXAS(page, limit, this.categoryId).subscribe(
      res => {
        this.page = page
        this.recipientsLoading = false;
        this.totalItems = res['data']['totalCount']
        this.recipients = res['data']['data']
      },
      err => {
        this.recipientsLoading = false;
        this.toastr.error(err.error.message, 'ERROR');
      }
    )
  }
  deleteRecipient = () => {
    this.isDeleting = true;
    this.store.dispatch(bulkPayActions.deleteRecipient({ recipientId: this.selectedRecipient.product_id }));
  };
  editRecipient = () => {
    this.isUpdating = true;
    let requestBody = new EditRecipientDto();
    requestBody = {
      product_id: this.selectedRecipient.product_id,
      merchant_id: this.miscService.getMerchantDetails().merchant_id,
      name: this.recipientForm.get('recipientName').value,
      card_num: this.selectedCardNumber || '',
      wallet: this.recipientForm.get('xcelAccount').value || '',
      country_code: this.selectedRecipient.country_code,
      scheme_code: this.miscService.formatCountryCode(this.selectedRecipient.country_code) + '004',
      payment_type: this.recipientForm.get('receiptAccountType').value,
      category_id: this.recipientForm.get('receiptCategory').value,
      amount: this.recipientForm.get('price').value.toString() || '',
      bank: {
        bank_name: this.selectedBank?.bank_name || '',
        bank_code: this.selectedBank?.bank_code || '',
        country_code: this.selectedBank?.country_code || '',
        account_no: this.recipientForm.get('accountNumber')?.value || '',
        sort_code: "",
        route_no: ""
      }
    };
    this.store.dispatch(bulkPayActions.editRecipient({ recipientDto: requestBody }))
  }

  loadingAuthorizers: boolean
  authorizers: Authorizer[]
  getAuthorizers = () => {
    this.isNotifying = true;
    this.authorizerService.getMerchantAuthorizers().subscribe(
      res => {
        this.authorizers = res['data']
        if (this.authorizers.length == 0) {
          this.router.navigate(['/app/settings/authorizers'])
          this.toastr.info('You have not setup any transaction authorizer on this account, setup up an authorizer to proceed')
        } else {
          this.notifyAuthorizers();
        }
      },
      err => {
        this.isNotifying = false;
        this.toastr.error('Error in fetching authorizers')
      }
    )
  }

  notifyAuthorizers = () => {
    this.isNotifying = true;
    let requestBody = new NotifyDto();
    const formattedPaymentList = this.paymentList.reduce((acc, payee) => {
      return [
        ...acc,
        {
          country_code: payee.country_code,
          wallet: payee.wallet,
          bank_routeNo: payee?.bank?.route_no || '',
          payment_type: payee.payment_type,
          card_num: payee.card_num,
          amount: payee.amount.toString(),
          bank_account_no: payee?.bank?.account_no || '',
          scheme_code: payee.scheme_code,
          bank_code: payee.bank.bank_code,
          bank_name: payee.bank.bank_name,
          bank_country_code: payee.bank.country_code,
          product_id: payee.product_id,
          bank_sort_code: payee.bank.sort_code,
          category_id: payee.category_id,
          name: payee.name,
          des_amount: payee.amount.toString(),
          rate: '1'
        }
      ]
    }, [])
    requestBody = {
      merchant_id: this.miscService.getMerchantDetails().merchant_id,
      amount: this.totalToPay.toString(),
      description: this.paymentForm.get('description').value,
      tranx_id: "bulkpay",
      payment_id: this.miscService.genRandomString(8),
      recipient_count: this.paymentList.length,
      src_wallet: this.selectedWallet.account_id,
      src_countryCode: this.miscService.getMerchantDetails().country_code,
      recipients: formattedPaymentList
    };
    this.currentPaymentId = requestBody.payment_id
    this.store.dispatch(transactionsActions.notifyAuthorizers({ notifyDto: requestBody }))
  }

  sendOTP = () => {
    this.sendingOTP = true
    this.store.dispatch(bulkPayActions.sendOTP({ accountIdentifier: this.selectedWallet.account_number }))
  }

  checkSignatures = () => {
    this.checkingSignatures = true;
    this.store.dispatch(transactionsActions.checkSignatures({ paymentId: this.currentPaymentId }))
  }

  status;
  updateTransactionStatus = (updateTransactionStatusDto, status) => {
    this.isUpdatingTransaction = true;
    this.isUpdatingTransactionFailed = false;
    if (status == '1')
      this.status = 'failed'
    else if (status == '2')
      this.status = 'unfinished'
    else if (status == '0')
      this.status = 'successful'
    this.store.dispatch(bulkPayActions.updateTransactionStatus({ updateTransactionStatusDto, transactionId: 'bulkpay', paymentId: this.currentPaymentId, status }))
  }

  failedToPay = [];
  dlCodesRequested: number = 0;
  getDlCodes = (retry) => {
    this.bulkPayInProcess = true;
    this.paymentList.map(payee => {
      let requestPayload;
      let payeeCountry = this.countries.find(country => payee.country_code === country.alpha2Code)?.alpha2Code
      console.log(this.countries);
      console.log(payee.country_code);

      if (payeeCountry == 'GB') {
        if (!retry) {
          requestPayload = {
            config_preset: 'SgKOr2mJp',
            transaction_params: {
              payeeId: payee.card_num ? payee.card_num : payee.bank?.sort_code + payee.bank?.account_no,
              payerId: this.selectedWallet.account_id,
              src_amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
              des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
            }
          }
        } else {
          requestPayload = {
            config_preset: 'SgKOr2mJp',
            transaction_params: {
              payeeId: payee.card_num ? payee.card_num : payee['bank_sort_code'] + payee['bank_account_no'],
              payerId: this.selectedWallet.account_id,
              src_amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
              des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
            }
          }
        }
      } else {
        if (!retry) {
          requestPayload = {
            config_preset: 'SgKOr2mJp',
            transaction_params: {
              payeeId: payee.card_num ? payee.card_num : payee.bank?.bank_code + payee.bank?.account_no,
              payerId: this.selectedWallet.account_id,
              src_amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
              des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
            }
          }
        } else {
          requestPayload = {
            config_preset: 'SgKOr2mJp',
            transaction_params: {
              payeeId: payee.card_num ? payee.card_num : payee['bank_code'] + payee['bank_account_no'],
              payerId: this.selectedWallet.account_id,
              src_amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
              des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
            }
          }
        }
      }
      if (payee['is_papss']) {
        requestPayload = {
          config_preset: 'SgKOr2mJp',
          transaction_params: {
            payeeId: payee['bank_code'] + payee['bank_account_no'],
            payerId: this.selectedWallet.account_id,
            src_amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
            des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
          }
        }
      }
      const dlCodeRequest$ = payee['is_papss']
        ? this.bulkpayService.generatePapssDLCode(requestPayload)
        : this.bulkpayService.generateDLCode(requestPayload);
      dlCodeRequest$.subscribe(
        res => {
          this.dlCodesRequested++
          let payload
          if (!retry) {
            payload = {
              otp_sid: this.otpSid,
              description: this.paymentForm.get('description').value,
              channel_id: "XCelBusiness",
              currency: this.countries.find(country => this.miscService.getMerchantDetails().country_code === country.callingCode).currencyCode,
              account_id: this.selectedWallet.account_id || '',
              src_scheme: this.miscService.formatCountryCode(this.miscService.getMerchantDetails().country_code) + '004',
              des_card_num: payee.card_num || payee?.bank?.account_no,
              src_country: this.countries.find(country => this.miscService.getMerchantDetails().country_code === country.callingCode)?.alpha2Code,
              to_provider_code: payee.bank?.bank_code || payee.bank?.sort_code || payee.bank_code || payee.bank_sort_code || '',
              type: payee.payment_type == 'Account' ? 'bank' : payee.payment_type == 'momo' ? 'momo' : 'wallet',
              amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
              rate: payee['is_papss'] ? payee.rate : (this.countries.find(country => payee.country_code === country.alpha2Code)?.alpha2Code == this.countries.find(country => this.miscService.getMerchantDetails().country_code === country.callingCode)?.alpha2Code ? '1' : payee.rate),
              dest_country: payee['is_papss'] ? payee.country_code : this.countries.find(country => payee.country_code === country.alpha2Code)?.alpha2Code,
              account_name: payee.name,
              des_currency: payee['is_papss'] ? payee['des_currency'] : (payee.country_code === 'CD' && payee.des_currency ? payee.des_currency : this.countries.find(country => payee.country_code === country.alpha2Code)?.currencyCode),
              sortCode: payee.bank?.sort_code || '',
              converted_amount: this.miscService.formatTo2DecimalPlaces(payee['is_papss'] ? payee.amount * payee.rate : payee.amount / payee.rate),
              OTP: this.otpForm.get('otp').value,
              des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
              merchant: false,
              dest_scheme: payee.scheme_code,
              dl_code: res['otp_sid'],
              merchant_id: payee.merchant_id || this.miscService.getMerchantDetails().merchant_id,
              payment_reference: payee.reference_id || ""

            }
          } else {
            console.log(this.miscService.getMerchantDetails());
            console.log(this.countries);

            payload = {
              otp_sid: this.otpSid,
              description: this.paymentForm.get('description').value,
              channel_id: "XCelBusiness",
              currency: this.countries.find(country => this.miscService.getMerchantDetails().country_code === country.callingCode).currencyCode,
              account_id: this.selectedWallet.account_id || '',
              src_scheme: this.miscService.formatCountryCode(this.miscService.getMerchantDetails().country_code) + '004',
              des_card_num: payee.card_num || payee['bank_account_no'],
              src_country: this.countries.find(country => this.miscService.getMerchantDetails().country_code === country.callingCode)?.alpha2Code,
              to_provider_code: payee.bank?.bank_code || payee.bank?.sort_code || payee['bank_code'] || payee['bank_sort_code'] || '',
              type: payee.payment_type == 'Account' ? 'bank' : payee.payment_type == 'momo' ? 'momo' : 'wallet',
              amount: this.miscService.formatTo2DecimalPlaces(payee.amount),
              rate: payee['is_papss'] ? payee.rate : (this.countries.find(country => payee.country_code === country.alpha2Code)?.alpha2Code == this.countries.find(country => this.miscService.getMerchantDetails().country_code === country.callingCode)?.alpha2Code ? '1' : payee.rate),
              dest_country: payee['is_papss'] ? payee.country_code : this.countries.find(country => payee.country_code === country.alpha2Code)?.alpha2Code,
              account_name: payee.name,
              des_currency: payee['is_papss'] ? payee['des_currency'] : (payee.country_code === 'CD' && payee.des_currency ? payee.des_currency : this.countries.find(country => payee.country_code === country.alpha2Code)?.currencyCode),
              sortCode: payee['bank_sort_code'] || '',
              converted_amount: this.miscService.formatTo2DecimalPlaces(payee['is_papss'] ? payee.amount * payee.rate : payee.amount / payee.rate),
              OTP: this.otpForm.get('otp').value,
              des_amount: this.miscService.formatTo2DecimalPlaces(payee.des_amount || payee.amount),
              merchant: payee.isMerchant ? true : false,
              dest_scheme: payee.scheme_code,
              dl_code: res['otp_sid'],
              merchant_id: payee.merchant_id || this.miscService.getMerchantDetails().merchant_id,
              payment_reference: payee.reference_id || ""
            }
          }
          if (payeeCountry === 'CD' && this.kycData) {
            const nameParts = (payee.name || '').trim().split(' ');
            (payload as any)['kyc'] = {
              ...this.kycData,
              receiver_first_name: nameParts[0] || '',
              receiver_last_name: nameParts.slice(1).join(' ') || '',
              receiver_nationality: 'CD'
            };
          }
          if (['IN', 'AE', 'CN'].includes(payeeCountry) && this.ezremitData) {
            (payload as any)['initiator'] = { ...this.ezremitData.initiator };
            (payload as any)['receiver'] = { ...this.ezremitData.receiver };
          }
          this.makePayment(payee, payload)
        },
        err => {
          this.dlCodesRequested++
          this.failedToPay.push({ product_id: payee.product_id })
          this.toastr.error(`Payment was not completed for ${payee.name}`, 'Transaction failed');
          if (this.paymentList.length === this.dlCodesRequested) {
            this.bulkPayInProcess = false;
            if (this.failedToPay.length === this.paymentList.length)
              this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '1');
            this.toggleOtpModal('close');
            this.otpForm.reset();
            this.paymentForm.reset();
            this.isPaymentPage = null;
            this.isNotifying = null;
            this.checkingSignatures = null;
            this.authorizersNotifed = null;
            this.sufficientBalance = null;
            this.paymentList = [];
            this.toastr.info('Check dashboard for failed transactions to reprocess', 'Bulk pay not completed');
          }

        }
      )
    })

  }

  generatePaymentReference() {
    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().replace(/[-:.TZ]/g, '').replace(/\s/g, '');
    const reference = `CUD${formattedDate}` + (Math.ceil(Math.random() * 10e13)).toString();
    return reference;
  }

  makePayment(payee: Recipient, payload) {
    this.bulkPayInProcess = true;
    let requestBody;
    // let payeeCountry = this.countries.find(country => payee.country_code === country.alpha2Code).alpha2Code
    requestBody = payload
    this.bulkpayService.payRecipient(requestBody, payee.scheme_code).subscribe(
      res => {
        this.transactionsDone++
        if (res['data']['code'] == '00') {
          this.toastr.success(`Payment to ${payee.name} successful`);
          if (this.paymentList.length === this.transactionsDone) {
            // if (this.failedToPay.length === this.paymentList.length)
            //   this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '1');
            // else if (this.failedToPay.length > 0 && this.failedToPay.length < this.paymentList.length)
            //   this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '2');
            // else if (this.failedToPay.length == 0)
            //   this.updateTransactionStatus([], '0');
            this.updateTransactionStatus([], '3');
            this.router.navigate(['/app/make-payment/transactions/all']);
            this.toggleOtpModal('close');
            this.bulkPayInProcess = false;
            this.otpForm.reset();
            this.paymentForm.reset();
            this.isPaymentPage = null;
            this.isNotifying = null;
            this.checkingSignatures = null;
            this.authorizersNotifed = null;
            this.sufficientBalance = null;
            this.paymentList = [];
            this.transactionsDone = 0
            // this.toastr.info('Payment completed');
          }
        }
        else if (res['data']['code'] == '31') {
          this.toastr.info(`Payment to ${payee.name} is pending`, res['data']['message']['en']);
          if (this.paymentList.length === this.transactionsDone) {
            this.toggleOtpModal('close');
            this.router.navigate(['/app/make-payment/transactions/all']);
            this.bulkPayInProcess = false;
            this.otpForm.reset();
            this.paymentForm.reset();
            this.isPaymentPage = false;
            this.isNotifying = false;
            this.checkingSignatures = false;
            this.authorizersNotifed = false;
            this.sufficientBalance = false;
            this.paymentList = [];
            this.transactionsDone = 0
          }
        } else {
          console.log("Else Clause from make payment ----------------")
          this.failedToPay.push({ product_id: payee.product_id })
          this.toastr.error(`Payment was not completed for ${payee.name}`, res['data']['message']['en']);
          if (this.paymentList.length === this.transactionsDone) {
            // if (this.failedToPay.length === this.paymentList.length)
            //   this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '1');
            // else if (this.failedToPay.length > 0 && this.failedToPay.length < this.paymentList.length)
            //   this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '2');
            this.router.navigate(['/app/make-payment/transactions/all']);
            this.toggleOtpModal('close');
            this.bulkPayInProcess = false;
            this.otpForm.reset();
            this.paymentForm.reset();
            this.isPaymentPage = null;
            this.isNotifying = null;
            this.checkingSignatures = null;
            this.authorizersNotifed = null;
            this.sufficientBalance = null;
            this.paymentList = [];
            this.transactionsDone = 0
            this.toastr.info('Check to reprocess failed or pending transactions', 'Paymemnt not completed');
          }
        }
      },
      err => {
        console.log("Response Error from make payment ----------------")
        this.transactionsDone++
        this.failedToPay.push({ product_id: payee.product_id })
        this.toastr.error(`Payment was not completed for ${payee.name}`, err.error.errors);
        if (this.paymentList.length === this.transactionsDone) {
          // if (this.failedToPay.length === this.paymentList.length)
          //   this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '1');
          // else if (this.failedToPay.length > 0 && this.failedToPay.length < this.paymentList.length)
          //   this.updateTransactionStatus({ failed_recipients: this.failedToPay }, '2');
          this.router.navigate(['/app/make-payment/transactions/all']);
          this.toggleOtpModal('close');
          this.bulkPayInProcess = false;
          this.otpForm.reset();
          this.paymentForm.reset();
          this.isPaymentPage = null;
          this.isNotifying = null;
          this.checkingSignatures = null;
          this.authorizersNotifed = null;
          this.sufficientBalance = null;
          this.paymentList = [];
          this.transactionsDone = 0
          // this.toastr.info('Check to reprocess failed or pending transactions', 'Paymemnt not completed');
        }
      }
    )
  }


  //Callbacks
  onSendOTPSuccessful = () => {
    this.actions$.pipe(
      ofType(bulkPayActions.sendOTPSuccess),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.sendingOTP = false
      this.otpSid = response.otpInfo.otp_sid
      this.toastr.success('A transaction OTP will be sent to your email')
    })
  }
  onUpdateTransactionStatusSuccessful = () => {
    this.actions$.pipe(
      ofType(bulkPayActions.updateTransactionStatusSuccess),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.isUpdatingTransaction = false;
      this.isUpdatingTransactionFailed = false;
      if (this.status == 'unfinished') {
        this.router.navigate(['/app/bulk-pay/bulk-report'])
      } else if (this.status == 'successful') {
        this.router.navigate(['/app/bulk-pay/successful'])
      } else if (this.status == 'failed') {
        this.router.navigate(['/app/bulk-pay/failed'])
      }
    })
  }
  onUpdateTransactionStatusFailed = () => {
    this.actions$.pipe(
      ofType(bulkPayActions.updateTransactionStatusFailed),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.isUpdatingTransaction = false;
      this.isUpdatingTransactionFailed = true;
    })
  }
  onCheckSignaturesSuccessful = () => {
    this.actions$.pipe(
      ofType(transactionsActions.checkSignaturesSuccess),
      takeUntil(this.unsubscribe$)
    ).subscribe((response: any) => {
      this.checkingSignatures = false;
      if (!response.signaturesResult.success) {
        this.toastr.info(response.message, 'Info');
        // this.toastr.info('Authorization Pending');
      } else {
        // this.updateTransactionStatus([], '3');
        this.togglePaymentModal('close');
        this.sendOTP();
        this.toggleOtpModal('open');
      }
    })
  }
  onCheckSignaturesFailed = () => {
    this.actions$.pipe(
      ofType(transactionsActions.checkSignaturesFailed),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.checkingSignatures = false;
    })
  }

  onFetchBalanceSuccessful = () => {
    this.actions$.pipe(
      ofType(bulkPayActions.fetchAccountBalanceSuccess),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      if (response.balance.currentBalance > this.totalToPay || response.balance.balance > this.totalToPay)
        this.sufficientBalance = true
      else {
        this.toastr.error('You cannot proceed with this payment', 'Insufficient Wallet Balance');
        this.sufficientBalance = false
      }
    })
  }
  onDeleteRecipientSuccessful = () => {
    this.actions$
      .pipe(ofType(bulkPayActions.deleteRecipientSuccess), takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.isDeleting = false;
        this.fetchRecipients();
        this.toggleDeleteModal('close');
        this.toastr.success('Recipient deleted', 'Success');
      });
  };
  onDeleteRecipientFailed = () => {
    this.actions$
      .pipe(ofType(bulkPayActions.deleteRecipientFailed), takeUntil(this.unsubscribe$))
      .subscribe((response) => {
        this.isDeleting = false;
      });
  };

  onEditRecipientSuccessful = () => {
    this.actions$.pipe(
      ofType(bulkPayActions.editRecipientSuccess),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.isUpdating = false;
      this.selectedBank = null;
      this.selectedCardNumber = null;
      this.recipientForm.reset();
      this.paymentList = [];
      this.toggleModal('', 'close');
      this.fetchRecipients();
      this.toastr.success('Recipient updated', 'Success')
    })
  }
  onEditRecipientFailed = () => {
    this.actions$.pipe(
      ofType(bulkPayActions.editRecipientFailed),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.isUpdating = false;
    })
  }

  onNotifySuccessful = () => {
    this.actions$.pipe(
      ofType(transactionsActions.notifyAuthorizersSuccess),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.isNotifying = false;
      this.authorizersNotifed = true;
      // this.togglePaymentModal('close');
      this.toastr.success('Authorizers have been notified', 'Success')
    })
  }
  onNotifyFailed = () => {
    this.actions$.pipe(
      ofType(transactionsActions.notifyAuthorizersFailed),
      takeUntil(this.unsubscribe$)
    ).subscribe(response => {
      this.isNotifying = false;
      this.toastr.error(response.error.error['message'], 'Error in notifying authorizers')
    })
  }
  //Template Interactions
  selectWallet = (e) => this.selectedWallet = this.wallets.find(wallet => wallet.account_id == e?.target?.value);

  checkValue = (e) => {
    this.recipientForm.get('recipientName').reset()
    if (e.target.value == 'Wallet') {
      this.recipientForm.get('accountNumber').reset()
      this.recipientForm.get('bank').reset()
    } else if (e.target.value == 'Account') {
      this.recipientForm.get('xcelAccount').reset()
    }
  }
  setUpForEdit = (recipient: Recipient) => {
    this.selectedRecipient = recipient;
    this.recipientForm.get('receiptAccountType').setValue(recipient.payment_type)
    this.recipientForm.get('price').setValue(recipient.amount)
    this.recipientForm.get('bank').setValue(recipient?.bank?.bank_code || '')
    this.recipientForm.get('accountNumber').setValue(recipient?.bank?.account_no || '')
    this.recipientForm.get('xcelAccount').setValue(recipient.wallet || '')
    this.recipientForm.get('recipientName').setValue(recipient.name || '')
    this.recipientForm.get('receiptCategory').setValue(recipient.category_id)
    this.fetchRecipientBanks(this.selectedRecipient.country_code)
    this.toggleModal('', 'open');
  }
  toggleModal = (modalId = '', action: string) => {
    if (action === 'open') {
      this.modalOpen = true;
    } else {
      this.modalOpen = false;
    }
  }
  deleteModalOpen
  toggleDeleteModal = (action: string) => {
    if (action === 'open') {
      this.deleteModalOpen = true;
    } else {
      this.deleteModalOpen = false;
    }
  }
  toggleOtpModal = (action: string) => {
    if (action === 'open') {
      this.otpModalOpen = true;
    } else {
      this.otpModalOpen = false;
    }
  }

  togglePaymentModal = (action: string) => {
    if (action === 'open') {
      this.paymentModalOpen = true;
    } else {
      this.paymentModalOpen = false;
    }
  }

  isInPaymentList = (recipient) => this.paymentList.includes(recipient._id)
  updatePaymentList = (recipient: Recipient) => {
    const isInArray = this.paymentList.find(payee => payee._id === recipient._id)
    if (!isInArray)
      this.paymentList.push(recipient)
    else {
      let index = this.paymentList.findIndex(payee => payee._id === recipient._id);
      this.paymentList.splice(index, 1);
    }
  }
  isAll: boolean

  selectAllRecipients = () => {
    this.paymentList = [...this.recipients]
    const checkboxes = document.getElementsByName('recipient');
    for (let i = 0, n = checkboxes.length; i < n; i++) {
      (checkboxes[i] as HTMLInputElement).checked = true;
    }
  }

  proceedToPayment = () => {
    const missingAmount = this.paymentList.find(payee => !payee.amount)
    if (missingAmount) {
      this.toastr.error('One or more of your recipients doesn\'t have an amount set', 'Sorry, cannot proceed to payment');
    } else {
      this.totalToPay = this.paymentList.reduce((sum, payee) => sum + payee.amount, 0)
      this.togglePaymentModal('open');
    }
  }

  proceedWithDlCodes = (retry: boolean) => {
    this.getDlCodes(retry);
  }
  uploadType: string = '';
  setType = (e: any) => {
    this.uploadType = e.target.value;
  };
  selectCreationTypeModalOpen: boolean = false;
  toggleCreationTypeModal = (action: string) => {
    if (action === 'open') {
      this.selectCreationTypeModalOpen = true;
    } else {
      this.selectCreationTypeModalOpen = false;
    }
  };

  goBack = () => {
    this.toggleBulkUploadTypeModal('close');
    this.toggleCreationTypeModal('open');
  };

  proceedWithType = () => {
    if (this.uploadType === 'bulk') {
      this.toggleCreationTypeModal('close');
      this.toggleBulkUploadTypeModal('open');
    }

    if (this.uploadType === 'single') {
      this.toggleCreationTypeModal('close');
      this.toggleSingleUploadTypeModal('open');
    }
  };
  bulkUploadModalOpen: boolean = false;
  toggleBulkUploadTypeModal = (action: string) => {
    if (action === 'open') {
      this.bulkUploadModalOpen = true;
    } else {
      this.bulkUploadModalOpen = false;
    }
  };
  createRecipientModalOpen: boolean = false;
  toggleSingleUploadTypeModal = (action: string) => {
    if (action === 'open') {
      this.createRecipientModalOpen = true;
    } else {
      this.createRecipientModalOpen = false;
    }
  };
  excelPayload = null;
  prepareExcelUpload = (e: any) => {
    this.excelPayload = e;
    let files = e.target.files;
    this.fileName = files[0].name;
    this.toastr.success(`${this.fileName} uploaded successfully`, 'SUCCESS');
  };


  uploadExcel(event) {
    this.newRecipientsArray = [];
    let files = event.target.files;
    if (files.length === 0) return;

    this.fileName = files[0].name;
    var mimeType = files[0].type;
    if (
      mimeType.match(/application\/vnd.ms-excel*/) == null &&
      mimeType.match(/application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet*/) == null
    ) {
      return;
    }

    if (files[0].size > 3000000) {
      return;
    }
    var reader = new FileReader();
    reader.onload = (_event) => {
      let arrOfArr = this.excelService.convertExcelFileToJsonArray(reader.result);
      this.newRecipientsArray = [].concat.apply([], arrOfArr).map(this.ConvertToDto.bind(this));

      this.newRecipientsArray.map((recipient) => {
        if (recipient.wallet) {
          this.miscService
            .validateXcelAccount(recipient.wallet, this.miscService.formatCountryCode(recipient.country_code) + '004')
            .subscribe(
              (res) => {
                recipient.name = `${res['data']['first_name']} ${res['data']['last_name']}`;
                recipient.card_num = `${res['data']['card_num']}`;
                recipient.scheme_code = `${res['data']['scheme_code']}`;
                this.createBulkRecipient(recipient);
              },
              (err) => {
                this.toastr.error(`Account validation failed for xcel account ${recipient.wallet}`);
              }
            );
        } else if (recipient.bank?.account_no) {
          this.miscService
            .validateAccount(recipient.bank.bank_code, recipient.bank.account_no, this.user.currency.iso_code_2)
            .subscribe(
              (res) => {
                recipient.name = `${res['data']['name']}`;
                recipient.scheme_code = this.miscService.formatCountryCode(recipient.country_code) + '004';
                this.createBulkRecipient(recipient);
              },
              (err) => {
                this.toastr.error(`Account validation failed for bank account ${recipient.bank.account_no}`);
              }
            );
        }
      });
    };
    reader.readAsBinaryString(files[0]);
  }

  ConvertToDto(element): RecipientBulkDto {
    const newRecipient: RecipientBulkDto = new RecipientBulkDto();
    if (element.xcelAccount) {
      newRecipient.merchant_id = this.miscService.getMerchantDetails().merchant_id;
      newRecipient.name = '';
      newRecipient.card_num = '';
      newRecipient.wallet = element.xcelAccount.toString() || '';
      newRecipient.country_code = element.countryCode.toString() || '';
      newRecipient.scheme_code = '';
      newRecipient.payment_type = 'Wallet';
      newRecipient.category_id = element.categoryId;
      newRecipient.amount = element.amount.toString() || '';
      newRecipient.currency_code = element.currency_code || '';
      newRecipient.bank = {
        bank_name: '',
        bank_code: '',
        country_code: '',
        account_no: '',
        route_no: '',
        sort_code: ''
      };
      return newRecipient;
    } else {
      newRecipient.merchant_id = this.miscService.getMerchantDetails().merchant_id;
      newRecipient.name = '';
      newRecipient.card_num = '';
      newRecipient.wallet = '';
      newRecipient.country_code = element.countryCode.toString() || '';
      newRecipient.scheme_code = '';
      newRecipient.payment_type = 'Account';
      newRecipient.category_id = element.categoryId;
      newRecipient.amount = element.amount.toString() || '';
      newRecipient.currency_code = element.currencyCode || '';
      newRecipient.bank = {
        bank_name: element.bankName || '',
        bank_code: element.bankCode || '',
        country_code: element.countryCode.toString() || '',
        account_no: element.bankAccountNo || '',
        route_no: '',
        sort_code: ''
      };
      return newRecipient;
    }
  }

  createBulkRecipient = (recipient: RecipientBulkDto) => {
    this.isCreating = true;
    let requestBody = new RecipientDto();
    requestBody = {
      merchant_id: recipient.merchant_id,
      name: recipient.name,
      card_num: recipient.card_num,
      wallet: recipient.wallet,
      country_code: recipient.country_code,
      scheme_code: recipient.scheme_code,
      payment_type: recipient.payment_type,
      src_name: '',
      category_id: recipient.category_id,
      amount: recipient.amount,
      bank: {
        bank_name: recipient.bank.bank_name,
        bank_code: recipient.bank.bank_code,
        country_code: recipient.country_code,
        account_no: recipient.bank.account_no,
        sort_code: '',
        route_no: ''
      }
    };
    this.bulkpayService.createRecipient(requestBody).subscribe(
      (res) => {
        this.isCreating = false;
        this.toastr.success('Recipient created', 'Success');
        this.toggleBulkUploadTypeModal('close');
        this.fetchRecipients();
      },
      (err) => {
        this.toastr.error(`Failed to create ${recipient.name} as a recipient`, 'Error');
        this.isCreating = false;
      }
    );
  };

  //   getUserKYC() {
  //     const kyc_id = this.miscService.getMerchantDetails().kyc_id;
  //     this.bulkpayService.getUserKYC(kyc_id).subscribe(
  //         (res: any) => {
  //             this.kycDetails = res["data"];
  //             console.log("KYC details fetched successfully:", this.kycDetails);
  //         },
  //         (error) => {
  //       console.log("Error fetching KYC details:", error);
  //         }
  //     );
  // }

  // UNSUBCRIBE FROM OBSERVABLES
  ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }
}
